import React, { useState } from "react";
import { CheckCircle2, Paperclip, X, ShieldCheck, PenLine, Download, FileUp } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiErrorDetail } from "../lib/api";
import SignatureModal from "./SignatureModal";
import AddressAutocomplete from "./AddressAutocomplete";
import HoneypotField from "./HoneypotField";
import { getCurrentBrand } from "../hooks/useBranding";

const REGIONES = [
  "Arica y Parinacota",
  "Tarapacá",
  "Antofagasta",
  "Atacama",
  "Coquimbo",
  "Valparaíso",
  "Metropolitana de Santiago",
  "Libertador General Bernardo O'Higgins",
  "Maule",
  "Ñuble",
  "Biobío",
  "La Araucanía",
  "Los Ríos",
  "Los Lagos",
  "Aysén",
  "Magallanes y de la Antártica Chilena",
];

const CARGOS = [
  "Administrador",
  "Comité",
];

const SEGUROS_OPTS = [
  "Incendio + Sismo",
  "Responsabilidad Civil",
  "D&O (Directores y Comité)",
  "Robo áreas comunes",
  "Equipos electrónicos",
  "Fidelidad funcionaria",
  "Todos los anteriores",
];

const VENCIMIENTOS = [
  "Vence en los próximos 30 días",
  "Vence en 30 a 60 días",
  "Vence en 60 a 90 días",
  "Vence en más de 90 días",
  "No tengo póliza vigente",
  "No sé / Necesito revisar",
];

const MAX_FILE_MB = 8;

function Label({ children, required }) {
  return (
    <label className="block text-[11px] font-semibold mb-1 uppercase tracking-[0.08em]" style={{ color: "var(--text-secondary)", fontFamily: "Manrope" }}>
      {children} {required && <span style={{ color: "var(--brand-accent)" }}>*</span>}
    </label>
  );
}

export default function SolicitudCotizacionForm() {
  const [form, setForm] = useState({
    nombre: "",
    rut_representante: "",
    telefono: "",
    email: "",
    region: "",
    cargo: "",
    razon_social: "",
    rut_comunidad: "",
    direccion: "",
    comuna_ciudad: "",
    vencimiento_poliza: "",
  });
  const [archivo, setArchivo] = useState(null); // { name, base64, mime }
  const [autoriza, setAutoriza] = useState(false);
  const [website, setWebsite] = useState(""); // honeypot
  const [busy, setBusy] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [signedBlob, setSignedBlob] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`El archivo supera ${MAX_FILE_MB} MB`);
      return;
    }
    const b64 = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(f);
    });
    const pure = String(b64).split(",")[1] || "";
    setArchivo({ name: f.name, base64: pure, mime: f.type || "application/octet-stream" });
  };

  const canSubmit =
    form.nombre.trim() &&
    form.rut_representante.trim() &&
    form.telefono.trim() &&
    form.email.trim() &&
    form.cargo &&
    form.razon_social.trim() &&
    form.rut_comunidad.trim() &&
    form.direccion.trim() &&
    autoriza;

  const phoneFmt = () =>
    form.telefono.trim().startsWith("+")
      ? form.telefono.trim()
      : `+56${form.telefono.trim().replace(/^0+/, "")}`;

  const buildTypedSignaturePNG = (name) => {
    const off = document.createElement("canvas");
    const w = 800;
    const h = 240;
    off.width = w;
    off.height = h;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, w, h);
    ctx.font = `600 96px "Dancing Script", "Brush Script MT", cursive`;
    ctx.fillStyle = "#0A2540";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.fillText(name || " ", w / 2, h / 2);
    return off.toDataURL("image/png").split(",")[1] || "";
  };

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    // Firma automática con el nombre escrito en letra cursiva
    const imagen_base64 = buildTypedSignaturePNG(form.nombre.trim());
    handleSignConfirm({
      tipo: "type",
      imagen_base64,
      nombre_escrito: form.nombre.trim(),
    });
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSignConfirm = async (firmaPayload) => {
    setSignOpen(false);
    if (busy) return;
    setBusy(true);
    const partnerBrand = getCurrentBrand();
    const partnerKey = partnerBrand?.key || null;
    try {
      // 1) Solicitud de cotización (con poliza opcional)
      const solicitudPayload = {
        nombre: form.nombre.trim(),
        telefono: phoneFmt(),
        email: form.email.trim(),
        region: form.region,
        cargo: form.cargo,
        seguros: [],
        cantidad_unidades: 0,
        vencimiento_poliza: form.vencimiento_poliza,
        poliza_archivo_nombre: archivo?.name || null,
        poliza_archivo_base64: archivo?.base64 || null,
        poliza_archivo_mime: archivo?.mime || null,
        website,
        partner: partnerKey,
        // El email consolidado lo manda /cartas-asignacion/firmar para evitar duplicados
        skip_notification: true,
      };
      // 2) Firma carta asignación
      const cartaPayload = {
        razon_social: form.razon_social.trim(),
        rut_comunidad: form.rut_comunidad.trim(),
        direccion: form.direccion.trim(),
        comuna_ciudad: form.comuna_ciudad.trim(),
        representante_nombre: form.nombre.trim(),
        representante_rut: form.rut_representante.trim(),
        representante_cargo: form.cargo,
        representante_telefono: phoneFmt(),
        representante_email: form.email.trim(),
        firma_tipo: firmaPayload.tipo,
        firma_imagen_base64: firmaPayload.imagen_base64,
        firma_nombre_escrito: firmaPayload.nombre_escrito || null,
        website,
        partner: partnerKey,
      };

      // Enviar solicitud PRIMERO (para que /cartas-asignacion/firmar pueda
      // encontrarla por email y mandar el email consolidado con todo).
      await http.post("/solicitud-cotizacion", solicitudPayload);
      const cartaRes = await http.post(
        "/cartas-asignacion/firmar",
        cartaPayload,
        { responseType: "blob" },
      );

      setSignedBlob(cartaRes.data);
      downloadBlob(
        cartaRes.data,
        `carta-asignacion-firmada-${(form.razon_social || "comunidad").replace(/\s+/g, "_")}.pdf`,
      );
      setSent(true);
      toast.success("Listo — carta firmada y solicitud enviada.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (detail) {
        msg = formatApiErrorDetail(detail);
      } else if (err?.message === "Network Error" || !err?.response) {
        msg = "No pudimos conectar con el servidor. Verifica tu conexión e inténtalo en unos segundos.";
      } else if (err?.response?.status >= 500) {
        msg = "Servidor temporalmente no disponible. Por favor inténtalo en 30 segundos.";
      } else {
        msg = "No pudimos completar el envío. Inténtalo nuevamente.";
      }
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reDownload = () => {
    if (!signedBlob) return;
    downloadBlob(
      signedBlob,
      `carta-asignacion-firmada-${(form.razon_social || "comunidad").replace(/\s+/g, "_")}.pdf`,
    );
  };


  if (sent) {
    return (
      <section id="solicitud-cotizacion" className="relative" style={{ background: "var(--bg-cream)", scrollMarginTop: "90px" }} data-testid="solicitud-form-section">
        <div className="container-x py-16 md:py-20">
          <div className="max-w-xl mx-auto bg-white rounded-2xl p-10 border text-center" style={{ borderColor: "var(--border-subtle)", boxShadow: "var(--shadow-card)" }}>
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-5" style={{ background: "rgba(34, 197, 94, 0.12)" }}>
              <CheckCircle2 size={28} style={{ color: "#16a34a" }}/>
            </div>
            <h3 className="text-2xl md:text-3xl font-extrabold mb-3" style={{ fontFamily: "Manrope" }}>
              ¡Listo! Carta firmada y solicitud enviada.
            </h3>
            <p className="text-base mb-6" style={{ color: "var(--text-muted)" }}>
              Un ejecutivo se contactará contigo en menos de 24 horas hábiles para revisar tu póliza y armar una propuesta a la medida.
            </p>
            {signedBlob && (
              <button
                onClick={reDownload}
                className="btn-outline mx-auto"
                style={{ padding: "0.75rem 1.4rem", fontSize: 14 }}
                data-testid="sent-redownload"
              >
                <Download size={15}/> Descargar copia de la carta firmada
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="solicitud-cotizacion" className="relative" style={{ background: "var(--bg-cream)", scrollMarginTop: "90px" }} data-testid="solicitud-form-section">
      <div className="container-x py-8 md:py-12">
        {/* Title */}
        <div className="max-w-6xl mx-auto text-center mb-6 md:mb-8">
          <h2 className="text-[17px] sm:text-[19px] md:text-[21px] lg:text-[22px] font-extrabold tracking-tight leading-[1.3] lg:whitespace-nowrap" style={{ fontFamily: "Manrope" }}>
            Llena el formulario{" "}
            <span style={{ color: "var(--brand-accent)" }}>→</span>{" "}
            un <span className="serif" style={{ color: "var(--brand-accent)", fontSize: "1.1em" }}>ejecutivo</span> te contacta{" "}
            <span style={{ color: "var(--brand-accent)" }}>→</span>{" "}
            mejoramos tu póliza y la <span className="serif" style={{ color: "var(--brand-accent)", fontSize: "1.1em" }}>activamos</span>.
          </h2>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          className="max-w-3xl mx-auto bg-white rounded-2xl p-5 md:p-7 border"
          style={{ borderColor: "var(--border-subtle)", boxShadow: "var(--shadow-card)" }}
          data-testid="solicitud-form"
        >
          <HoneypotField value={website} onChange={setWebsite} />

          {/* === Sección 1: Datos del representante === */}
          <SectionTitle index="01" title="Datos del representante" subtitle=""/>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3.5">
            <div className="sm:col-span-2">
              <Label required>Nombre completo</Label>
              <input
                className="field-input field-input--compact"
                value={form.nombre}
                onChange={(e) => set("nombre", e.target.value)}
                placeholder="Tu nombre completo"
                required
                data-testid="sol-nombre"
              />
            </div>

            <div>
              <Label required>RUT</Label>
              <input
                className="field-input field-input--compact"
                value={form.rut_representante}
                onChange={(e) => set("rut_representante", e.target.value)}
                placeholder="12.345.678-9"
                required
                data-testid="sol-rut-representante"
              />
            </div>

            <div>
              <Label required>Cargo en la comunidad</Label>
              <select
                className="field-input field-input--compact"
                value={form.cargo}
                onChange={(e) => set("cargo", e.target.value)}
                required
                data-testid="sol-cargo"
              >
                <option value="">Seleccionar</option>
                {CARGOS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div>
              <Label required>Teléfono</Label>
              <div className="flex items-stretch gap-2">
                <div
                  className="inline-flex items-center px-2.5 rounded-lg border text-[12.5px] font-semibold whitespace-nowrap"
                  style={{ borderColor: "var(--border-medium)", background: "var(--bg-soft)", color: "var(--text-secondary)" }}
                >
                  <span className="mr-1" aria-hidden>🇨🇱</span> +56
                </div>
                <input
                  type="tel"
                  className="field-input field-input--compact flex-1"
                  value={form.telefono}
                  onChange={(e) => set("telefono", e.target.value.replace(/[^\d\s]/g, ""))}
                  placeholder="9 1234 5678"
                  required
                  data-testid="sol-telefono"
                />
              </div>
            </div>

            <div>
              <Label required>Correo electrónico</Label>
              <input
                type="email"
                className="field-input field-input--compact"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="tu@email.cl"
                required
                data-testid="sol-email"
              />
            </div>
          </div>

          {/* === Sección 2: Datos de la comunidad === */}
          <SectionTitle index="02" title="Datos de la comunidad" subtitle="Información para la carta de asignación"/>
          <div className="grid sm:grid-cols-2 gap-x-4 gap-y-3.5">
            <div>
              <Label required>Razón social</Label>
              <input
                className="field-input field-input--compact"
                value={form.razon_social}
                onChange={(e) => set("razon_social", e.target.value)}
                placeholder="Comunidad Edificio Las Higueras"
                required
                data-testid="sol-razon-social"
              />
            </div>

            <div>
              <Label required>RUT de la comunidad</Label>
              <input
                className="field-input field-input--compact"
                value={form.rut_comunidad}
                onChange={(e) => set("rut_comunidad", e.target.value)}
                placeholder="76.123.456-7"
                required
                data-testid="sol-rut-comunidad"
              />
            </div>

            <div className="sm:col-span-2">
              <Label required>Dirección</Label>
              <AddressAutocomplete
                value={form.direccion}
                onChange={(v) => set("direccion", v)}
                onSelect={(item) => {
                  setForm((f) => ({
                    ...f,
                    direccion: item.display,
                    comuna_ciudad: item.comuna || f.comuna_ciudad,
                    region: item.region || f.region,
                  }));
                }}
                placeholder="Av Apoquindo 1234, Las Condes"
                testId="sol-direccion"
              />
              <div className="mt-2 grid grid-cols-2 gap-2.5">
                <input
                  type="text"
                  value={form.comuna_ciudad}
                  onChange={(e) => set("comuna_ciudad", e.target.value)}
                  placeholder="Comuna *"
                  className="field-input field-input--compact"
                  required
                  data-testid="sol-comuna"
                />
                <input
                  type="text"
                  value={form.region}
                  onChange={(e) => set("region", e.target.value)}
                  placeholder="Región"
                  className="field-input field-input--compact"
                  data-testid="sol-region"
                />
              </div>
            </div>
          </div>

          {/* === Sección 3: Tu póliza === */}
          <SectionTitle index="03" title="Opcional" subtitle=""/>

          {/* Drop zone destacado */}
          <div className="mb-3.5">
            {!archivo ? (
              <label
                className="flex flex-col items-center justify-center gap-2 px-6 py-7 rounded-xl border-2 cursor-pointer transition hover:bg-[var(--bg-soft)]"
                style={{ borderColor: "var(--brand-accent)", borderStyle: "dashed", background: "rgba(238, 81, 71, 0.03)" }}
                data-testid="sol-archivo-label"
              >
                <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: "rgba(238, 81, 71, 0.12)" }}>
                  <FileUp size={20} style={{ color: "var(--brand-accent)" }}/>
                </div>
                <div className="text-center">
                  <div className="text-[14.5px] font-extrabold" style={{ fontFamily: "Manrope" }}>
                    Carga tu póliza para revisarla y mejorarla
                  </div>
                  <div className="text-[12.5px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                    Acepta PDF, JPG o PNG (máx. {MAX_FILE_MB} MB) · Opcional
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={onFile}
                  data-testid="sol-archivo-input"
                />
              </label>
            ) : (
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border-2" style={{ borderColor: "rgba(34,197,94,0.4)", background: "rgba(34,197,94,0.05)" }}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(34,197,94,0.15)" }}>
                    <Paperclip size={15} style={{ color: "#15803d" }}/>
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>{archivo.name}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Póliza cargada · listo para revisar</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setArchivo(null)}
                  className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition hover:bg-white"
                  aria-label="Quitar archivo"
                  data-testid="sol-archivo-remove"
                >
                  <X size={14}/>
                </button>
              </div>
            )}
          </div>

          {/* Vencimiento (opcional) */}
          <div>
            <Label>Vencimiento de póliza actual <span className="opacity-50 normal-case font-normal tracking-normal">(opcional)</span></Label>
            <select
              className="field-input field-input--compact"
              value={form.vencimiento_poliza}
              onChange={(e) => set("vencimiento_poliza", e.target.value)}
              data-testid="sol-vencimiento"
            >
              <option value="">Selecciona</option>
              {VENCIMIENTOS.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>

          {/* === Sección 4: Autorización === */}
          <SectionTitle index="04" title="Autorización" subtitle=""/>
          <label className="flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition hover:bg-[var(--bg-soft)]" style={{ borderColor: autoriza ? "var(--brand-accent)" : "var(--border-medium)", background: autoriza ? "rgba(238,81,71,0.04)" : "transparent" }}>
            <input
              type="checkbox"
              checked={autoriza}
              onChange={(e) => setAutoriza(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[var(--brand-accent)] flex-shrink-0"
              data-testid="sol-autoriza"
              required
            />
            <span className="text-[13px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Declaro que autorizo a Mi Comunidad Segura, registrados en la CMF bajo el código 9860, para que nos represente como corredor en la cotización, mejora y contratación de las pólizas de mi comunidad.
              <br/>
              <span className="text-[11.5px] block mt-1" style={{ color: "var(--text-muted)" }}>
                Al continuar nos autorizarás vía carta de asignación electrónica. Con vigencia revocable en cualquier momento.
              </span>
            </span>
          </label>

          <button
            type="submit"
            className="btn-primary w-full justify-center mt-5"
            disabled={!canSubmit || busy}
            data-testid="sol-submit"
            style={{ padding: "0.85rem 1.4rem", fontSize: 14 }}
          >
            {busy ? "Procesando..." : <><PenLine size={15}/> Confirmar y solicitar cotización</>}
          </button>

          <div className="mt-3 flex items-center gap-2 text-[11.5px]" style={{ color: "var(--text-soft)" }}>
            <ShieldCheck size={12}/>
            <span>Datos protegidos bajo Ley 19.628. Firma electrónica simple (Ley 19.799).</span>
          </div>
        </form>
      </div>

      <SignatureModal
        open={signOpen}
        onClose={() => setSignOpen(false)}
        defaultName={form.nombre}
        onConfirm={(p) => handleSignConfirm(p)}
      />
    </section>
  );
}

function SectionTitle({ index, title, subtitle }) {
  return (
    <div className="mt-6 mb-3.5 pb-2 border-b first:mt-0" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="flex items-baseline gap-2.5">
        <span className="text-[10px] font-extrabold tracking-[0.1em]" style={{ color: "var(--brand-accent)", fontFamily: "Manrope" }}>{index}</span>
        <h3 className="text-[15px] font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{title}</h3>
      </div>
      {subtitle && <p className="text-[11.5px] mt-0.5 ml-7" style={{ color: "var(--text-muted)" }}>{subtitle}</p>}
    </div>
  );
}

